<template>
  <page-wrapper :title="title" :has-pad="true">
    <div class="wrapper">
      <div class="d-flex justify-content-between">
        <sc-button
          v-if="!isViewOnlyByRole"
          :route="{ name: 'ServiceUnavailableDatePage', params: $route.params }"
          class="mb-4"
          variant="secondary"
          >ลงวันไม่ว่าง</sc-button
        >
        <language-select
          v-model="language"
          @change="handleChangeLanguage"
        ></language-select>
      </div>
      <service-editor
        v-if="!isLoading"
        v-model="form"
        :icon-url="iconUrl"
        :disabled="isViewOnlyByRole"
        class="editor"
        @removeDuration="handleRemoveDuration"
        @addDuration="handleAddDuration"
        @isCheckCombineOption="handleCheckCombineOption"
      ></service-editor>
      <div v-if="!isViewOnlyByRole" class="d-flex justify-content-between">
        <sc-button variant="danger" @click="handleClickRemove">ลบ</sc-button>
        <sc-button variant="primary" @click="handleClickSaveService">บันทึก</sc-button>
      </div>
      <div>
        <service-confirm-material-code
          v-if="isShowConfirmMaterialCode"
          v-model="form"
          @confirmMaterialCode="handleConfirmMaterialCode"
        ></service-confirm-material-code>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
import PageWrapper from "@/lib/components/PageWrapper";
import ScButton from "@/lib/components/ScButton";
import LanguageSelect from "@/lib/components/LanguageSelect";
import { baanRueJaiClient } from "@/dependencies";
import { uploadPublicImage } from "@/god";
import { localization , serviceType as serviceTypeMapper } from "@/lib/mapper";
import { isNil } from "ramda";
import { DateTime } from "luxon";
import { MessageBox } from "element-ui";
import { debounce } from "@/lib/util";
import {
  convertBahtToSatangAndRoundTwoDecimal,
  convertBathToStangAndRoundTwoDecimal
} from "@/lib/formatter";
import ServiceEditor from "../components/ServiceEditor";
import ServiceConfirmMaterialCode from "../components/ServiceConfirmMaterialCode";
import { formatServicePriceInputValue } from "../utils/servicePrice";
import serviceFormMixin from "../mixins/serviceFormMixin";
import {
  attachServiceProductsToVariants,
  getDetailServiceFormValidations,
  getPrimaryServiceProduct,
  getServiceProductCostSummary,
  getServiceProductSelectorValue,
  getServiceMaterialFieldsForApi,
  getServiceOptionsPayloadForApi,
  getServiceProductsFieldForApi,
  normalizeServiceProducts,
  normalizeServiceOptionCostsForForm,
  formatDurationsForApi,
} from "../utils/serviceForm";

export default {
  components: {
    PageWrapper,
    ScButton,
    ServiceEditor,
    LanguageSelect,
    "service-confirm-material-code": ServiceConfirmMaterialCode
  },
  mixins: [serviceFormMixin],
  data() {
    const title = "รายละเอียดบริการ";
    const form = {
      name: "",
      shortDescription: "",
      minimumAdvanceDuration: 5,
      quota: 0,
      cost: null,
      provider: null,
      externalLink: null,
      description: "",
      termsAndConditions: "",
      iconFile: null,
      serviceOptions: null,
      appointDurationSlots: [],
      appointDateConstraints: [],
      serviceOptionCosts: null,
      hasOptionSwitch: true,
      hasEditOptionCost: false,
      sapServiceType: "NORMAL",
      serviceType: serviceTypeMapper.SERVICE,
      serviceProducts: []
    };
    const iconUrl = null;
    const isLoading = true;
    const currentQuota = form.quota;
    const permission = JSON.parse(localStorage.getItem("adminRolePermission")) || null;

    return {
      title,
      form,
      iconUrl,
      isLoading,
      language: localization.TH,
      currentQuota,
      isServiceOptionCosts: false,
      isCheckCombineOption: false,
      materialCodeList: [],
      isShowConfirmMaterialCode: false,
      permission
    };
  },
  computed: {
    isEditorDisabled() {
      return this.$isCallCenter;
    },
    isViewOnlyByRole() {
      const roles = ["AC","CC"]
      return roles.includes(this.permission.role)
    }
  },
  watch: {
    form(form, oldForm) {
      if (this.form.iconFile === oldForm.iconFile) {
        return;
      }

      this.iconUrl = URL.createObjectURL(this.form.iconFile);
    },
    "form.quota": debounce(function watchFormQuota(newQuota) {
      if (
        newQuota > this.currentQuota &&
        newQuota !== this.form.appointDurationSlots.length
      ) {
        this.handleAddAppointDurationSlot(
          newQuota,
          this.currentQuota,
          this.form.appointCost
        );
        this.handleAddDateConstraints(newQuota, this.currentQuota);
      } else if (newQuota < this.currentQuota) {
        this.handleRemoveAppointDurationSlot(newQuota, this.currentQuota);
        this.handleRemoveDateConstraints(newQuota, this.currentQuota);
      }
      this.currentQuota = newQuota;
    }, 0.5)
  },
  created() {
    this.setup();
  },
  methods: {
    getRouteServiceId() {
      const { id } = this.$route.params;
      if (!id) {
        this.$message.error("ไม่พบรหัสบริการ");
        return null;
      }

      return id;
    },
    async setup() {
      await this.setupData();
      this.isLoading = false;
    },
    async setupData() {
      await this.setupMaterialCode();
      await this.setupForm();
    },
    async setupForm() {
      const data = await this.fetchData();
      if (!data) {
        return;
      }

      const {
        name: dataName,
        short_description: shortDescription,
        minimum_advance_duration: minimumAdvanceDuration,
        quota,
        cost,
        provider,
        description,
        terms_and_conditions: termsAndConditions,
        external_link: externalLink,
        icon_url: dataIconUrl,
        appoint_duration_slots: rawAppointDurationSlots,
        appoint_date_constraints: rawAppointDateConstraints,
        appoint_cost: appointCost,
        packages_by_service: packagesByService,
        options,
        material_code: materialCode,
        material_quantity: materialQuantity,
        sap_service_type: sapServiceType,
        sap_service_recognize_revenue: sapServiceRecognizeRevenue,
        is_vat_registered: isVatRegistered,
        service_type: selectedServiceType,
        service_products: rawServiceProducts
      } = data;

      const serviceProducts = normalizeServiceProducts(rawServiceProducts);
      const selectedServiceProduct = getPrimaryServiceProduct(serviceProducts);
      const serviceProductSelectorValue = getServiceProductSelectorValue({
        product: selectedServiceProduct,
      });
      const serviceProductCostSummary =
        getServiceProductCostSummary(serviceProductSelectorValue);
      const serviceProductCost = serviceProductCostSummary.cost;

      const hasServiceProduct =
        selectedServiceType === serviceTypeMapper.DOTLIFE_PRODUCT;

      const appointDateConstraints = rawAppointDateConstraints.map(
        (appointDateConstraint) => {
          const {
            slot_number: slotNumber,
            reference_date_type: referenceDateType,
            offset,
            type
          } = appointDateConstraint;

          const constraints = offset.values.days;

          return {
            slotNumber,
            referenceDateType,
            constraints,
            type
          };
        }
      );

      const appointCostReadOnly = !!(appointCost && quota >= 1);

      const appointDurationSlots = this.setupAppointDurationSlots(
        rawAppointDurationSlots,
        appointCost
      );

      const packagesByServiceList = packagesByService.map((item) => {
        const {
          id,
          name: packageName,
          price,
          billing_cycle: billingCycle,
          target,
          detail,
          image: imageUrl,
          original_price: originalPrice
        } = item;
        const route = { name: "PackageDetailPage", params: { id } };

        return {
          id,
          imageUrl,
          name: packageName,
          price,
          billingCycle,
          target,
          detail,
          originalPrice,
          route,
        };
      });

      const rawServiceOptions = options && options.service_options
        ? options.service_options.map((serviceOption) => {
          const variants = serviceOption.variants.map((variant) => {
            const MaterialDescription = this.findMaterialDescription(variant.material_code)

            let codeDescriptionUnit = null
            let unitName = null
            if (MaterialDescription) {
              codeDescriptionUnit = MaterialDescription.code_description_unit
              unitName = MaterialDescription.unitName
            }

            return {
              ...variant,
              code_description_unit: codeDescriptionUnit,
              unit_name: unitName
            }
          })

          return {
            ...serviceOption,
            variants
          }
        }
        )
        : undefined;

      const currentServiceOptionCosts = options
        && options.service_option_costs
        && options.service_option_costs.length > 0
        ? options.service_option_costs
        : null;
      const serviceOptionCosts = normalizeServiceOptionCostsForForm({
        serviceOptionCosts: currentServiceOptionCosts,
        hasServiceProduct,
      });

      const serviceOptions = attachServiceProductsToVariants(
        rawServiceOptions,
        serviceProducts,
        serviceOptionCosts
      );

      if (options) {
        this.isServiceOptionCosts = true;
      }

      const hasDeleteOption = !!options;

      const newProvider = {
        id: provider.id,
        isVatRegistered: provider.is_vat_registered,
        logoUrl: provider.logo_url,
        name: provider.name
      }

      this.form = {
        name: dataName,
        shortDescription,
        minimumAdvanceDuration: isNil(minimumAdvanceDuration)
          ? 5
          : minimumAdvanceDuration,
        quota,
        cost: hasServiceProduct && !serviceOptions && !isNil(serviceProductCost)
          ? serviceProductCost
          : convertBathToStangAndRoundTwoDecimal(cost),
        provider: newProvider,
        externalLink,
        description,
        termsAndConditions,
        iconFile: null,
        appointDurationSlots,
        appointDateConstraints,
        appointCost,
        appointCostReadOnly,
        packagesByServiceList,
        serviceOptions,
        serviceOptionCosts,
        hasDeleteOption,
        hasOptionSwitch: true,
        isCanNotEditService: true,
        hasEditOptionCost: false,
        materialCode: materialCode || null,
        materialQuantity: materialQuantity || null,
        sapServiceType: sapServiceType || "NORMAL",
        sapServiceRecognizeRevenue,
        isVatRegistered,
        serviceType: selectedServiceType,
        hasServiceProduct: !!hasServiceProduct,
        serviceProductId: serviceProductSelectorValue
          ? serviceProductSelectorValue.id
          : null,
        serviceProductName: serviceProductSelectorValue
          ? serviceProductSelectorValue.name
          : "",
        serviceProductCode: serviceProductSelectorValue
          ? serviceProductSelectorValue.code
          : "",
        serviceProductQuantity: !serviceProductSelectorValue ||
          isNil(serviceProductSelectorValue.stock)
          ? null
          : serviceProductSelectorValue.stock,
        serviceProductCost: isNil(serviceProductCost)
          ? null
          : serviceProductCost,
        serviceProducts
      };

      this.iconUrl = dataIconUrl;
    },
    async fetchData() {
      const id = this.getRouteServiceId();
      if (!id) {
        return null;
      }

      const response =
        this.language === localization.TH
          ? await baanRueJaiClient.admin.subscriptions.services.get(id)
          : await baanRueJaiClient.admin.subscriptions.services.getLocalization(
              id,
              this.language
            );

      return response.data.result;
    },
    setupAppointDurationSlots(appointDurationSlots, appointmentCosts) {
      const newAppointDurationSlots = appointDurationSlots.map((slot, index) => {
        const slotNumber = slot.slot_number;
        const durations = this.setupDurations(slot.durations);
        const defaultDurations = this.setupDurations(slot.default_durations);
        const isDefaultDuration = slot.is_default_duration;
        const appointmentCost = appointmentCosts.find(
          (appoint) => appoint.appointment_no === index + 1
        );
        const appointCost = appointmentCost
          ? this.formatAppointCostForInput(appointmentCost.cost)
          : null;

        return {
          slotNumber,
          durations,
          defaultDurations,
          isDefaultDuration,
          appointCost
        };
      });

      return newAppointDurationSlots;
    },
    setupDurations(durations) {
      const formattedDurations = durations.map(({ start_at: rawStartAt, end_at: rawEndAt }) => {
        const startAt = DateTime.fromISO(rawStartAt).toFormat("HH:mm");
        const endAt = DateTime.fromISO(rawEndAt).toFormat("HH:mm");

        return { startAt, endAt };
      });

      return formattedDurations;
    },
    formatAppointCostForInput(cost) {
      const bahtCost = convertBathToStangAndRoundTwoDecimal(cost);

      return isNil(bahtCost)
        ? null
        : formatServicePriceInputValue(bahtCost, true);
    },
    setupAppointDurationSlotsForUpdate(appointDurationSlots) {
      const newAppointDurationSlots = appointDurationSlots.map((slot) => {
        const slotNum = slot.slotNumber;
        const durations = slot.isDefaultDuration
          ? formatDurationsForApi(slot.defaultDurations)
          : formatDurationsForApi(slot.durations);
        const slotAppointCost = convertBahtToSatangAndRoundTwoDecimal(slot.appointCost);

        return {
          slot_number: slotNum,
          durations,
          appointCost: slotAppointCost,
        };
      });

      return newAppointDurationSlots;
    },
    countAppointCost(quota, appointmentCost) {
      const appointCost = appointmentCost.find(
        (cost) => quota === cost.appointment_no
      );

      return appointCost
        ? this.formatAppointCostForInput(appointCost.cost)
        : null;
    },
    getValidations() {
      return getDetailServiceFormValidations({
        form: this.form,
        hasEmptyDuration: () => this.hasEmptyDuration(),
        hasEmptyCombineOption: () => this.hasEmptyCombineOption(),
        hasEmptyOptionCost: () => this.hasEmptyMainCombineOption(),
        hasEmptyExtraOptionCost: () => this.hasEmptyExtraCombineOption(),
        isCheckCombineOption: this.isCheckCombineOption,
        shouldValidateOptionCosts: () =>
          this.isServiceOptionCosts && this.form.serviceOptionCosts,
        minimumAdvanceDurationMessage:
          "ไม่สามารถบันทึกบริการได้เนื่องจากจำนวนวันนัดล่วงหน้าน้อยกว่า 1 วัน",
      });
    },
    async handleClickSaveService() {
      const validations = this.getValidations();
      const hasFailedValidation = validations.some((v) => {
        if (v.check()) {
          this.$message.error(v.message);
          return true;
        }
        return false;
      });
      if (hasFailedValidation) {
        return;
      }

      this.isShowConfirmMaterialCode = true;
    },
    async handleClickSave() {
      try {
        await this.updateService();
        this.$router.push({ name: "ServiceListPage" });
      } catch (err) {
        this.$message.error("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
        console.error(err);
      }
    },
    handleChangeLanguage() {
      this.setup();
    },
    async updateService() {
      const id = this.getRouteServiceId();
      if (!id) {
        return undefined;
      }
      const {
        name: formName,
        shortDescription,
        minimumAdvanceDuration,
        quota,
        cost,
        provider,
        externalLink,
        description,
        termsAndConditions,
        iconFile,
        appointDurationSlots,
        serviceOptions,
        serviceOptionCosts,
        hasServiceProduct,
        materialCode,
        materialQuantity,
        sapServiceType,
        serviceType,
        serviceProducts: selectedServiceProducts
      } = this.form;

      const options = getServiceOptionsPayloadForApi({
        serviceOptions,
        serviceOptionCosts,
      });
      const serviceProductsField = getServiceProductsFieldForApi({
        serviceProducts: selectedServiceProducts,
        serviceOptions,
        serviceOptionCosts,
        hasServiceProduct,
        serviceType,
        includeEmpty: true,
      });
      const serviceMaterialFields = getServiceMaterialFieldsForApi({
        materialCode,
        materialQuantity,
        serviceOptions,
      });

      const formattedDurationSlots = this.setupAppointDurationSlotsForUpdate(
        appointDurationSlots
      );

      const params = {
        name: formName,
        short_description: shortDescription,
        minimum_advance_duration: minimumAdvanceDuration,
        quota,
        cost: convertBahtToSatangAndRoundTwoDecimal(cost),
        provider_id: provider.id,
        external_link: externalLink,
        description,
        terms_and_conditions: termsAndConditions,
        options,
        appoint_duration_slots: formattedDurationSlots,
        appoint_date_constraints: [],
        ...serviceMaterialFields,
        ...serviceProductsField,
        sap_service_type: sapServiceType,
        service_type: serviceType
      };

      if (iconFile) {
        params.icon = await uploadPublicImage(iconFile);
      }

      const response =
        this.language === localization.TH
          ? await baanRueJaiClient.admin.subscriptions.services.put(id, params)
          : await baanRueJaiClient.admin.subscriptions.services.putLocalization(
              id,
              params,
              this.language
            );

      return response.data.result;
    },
    async handleClickRemove() {
      try {
        await MessageBox.confirm(
          "ยืนยันการลบบริการ",
          { confirmButtonText: "ตกลง", cancelButtonText: "ยกเลิก" }
        );
      } catch { return; }

      try {
        await this.removeService();
        this.$router.push({ name: "ServiceListPage" });
      } catch (err) {
        this.$message.error("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
        console.error(err);
      }
    },
    async removeService() {
      const id = this.getRouteServiceId();
      if (!id) {
        return;
      }

      await baanRueJaiClient.admin.subscriptions.services.delete(id);
    },
    async setupMaterialCode() {
      const response = await baanRueJaiClient.admin.sap.codeList();
      this.materialCodeList = response.data.result.materials;
    },
    findMaterialDescription(material) {
      if (!this.materialCodeList || this.materialCodeList.length === 0) {
        return null
      };

      const materialSelected = this.materialCodeList.find(
        (materialCode) => materialCode.code === material
      );

      if (!materialSelected) return null;

      const materialSelectedSplitString = materialSelected.code_description_unit.split(" ");
      const unitName = materialSelectedSplitString[materialSelectedSplitString.length - 1];

      return {
        code_description_unit: materialSelected.code_description_unit,
        unitName
      };
    },
    handleConfirmMaterialCode(value) {
      if (value) {
        this.handleClickSave();
      }
      this.isShowConfirmMaterialCode = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.wrapper {
  padding: $space 0;
}

.editor {
  margin-bottom: $space;
}
</style>
